import React, { useState, useEffect } from 'react';
import { FaChartLine, FaCalendarAlt, FaChartBar, FaExclamationTriangle, FaCheckCircle, FaUsers } from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import './PlanManager.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const PlanManager = ({ username }) => {
  const { t, language } = useLanguage();
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = async () => {
    if (!username) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${STRANDS_API_URL}/api/user/usage?username=${encodeURIComponent(username)}`
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos de uso');
      }

      const data = await response.json();

      if (data.success) {
        setUsage(data.usage);
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (err) {
      console.error('[PlanManager] Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchUsage();
    }
  }, [username]);

  // Formatear fecha
  const formatResetDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Obtener el mes actual
  const getCurrentMonth = () => {
    const now = new Date();
    return now.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric' });
  };

  // Determinar color de la barra según el estado
  const getProgressColor = (status) => {
    switch (status) {
      case 'exceeded':
        return '#dc2626';
      case 'warning':
        return '#f59e0b';
      default:
        return '#10b981';
    }
  };

  // Obtener icono según estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'exceeded':
        return <FaExclamationTriangle className="status-icon exceeded" />;
      case 'warning':
        return <FaExclamationTriangle className="status-icon warning" />;
      default:
        return <FaCheckCircle className="status-icon ok" />;
    }
  };

  // Obtener mensaje según estado
  const getStatusMessage = (status, remaining) => {
    switch (status) {
      case 'exceeded':
        return t('admin.orgExceededLimit');
      case 'warning':
        return `${remaining} ${t('admin.queriesLeft')}`;
      default:
        return `${remaining} ${t('admin.queriesLeft')}`;
    }
  };

  return (
    <div className="plan-manager">
      {/* Header */}
      <div className="plan-manager-header">
        <div className="header-title">
          <FaChartBar className="header-icon" />
          <h3>{t('admin.planTitle')}</h3>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={fetchUsage}>{t('admin.retry')}</button>
        </div>
      )}

      {/* Main Content */}
      {isLoading ? (
        <div className="plan-loading">
          <div className="spinner"></div>
          <p>{t('admin.loadingPlan')}</p>
        </div>
      ) : usage ? (
        <>
          {/* Card Principal de Uso */}
          <div className={`usage-card ${usage.status || 'ok'}`}>
            <div className="usage-header">
              <div className="usage-title">
                <FaChartLine className="usage-icon" />
                <span>{t('admin.monthlyQueries')}</span>
              </div>
              {getStatusIcon(usage.status)}
            </div>

            {/* Números grandes */}
            <div className="usage-numbers">
              <span className="current-count">{usage.monthly_count || 0}</span>
              <span className="separator">/</span>
              <span className="limit-count">{usage.monthly_limit || 1000}</span>
            </div>

            {/* Barra de progreso */}
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{
                  width: `${Math.min(usage.percentage || 0, 100)}%`,
                  backgroundColor: getProgressColor(usage.status)
                }}
              />
            </div>

            {/* Porcentaje */}
            <div className="usage-percentage">
              {usage.actual_percentage?.toFixed(1) || 0}% {t('admin.used')}
            </div>

            {/* Mensaje de estado */}
            <div className={`status-message ${usage.status || 'ok'}`}>
              {getStatusMessage(usage.status, usage.remaining)}
            </div>
          </div>

          {/* Info adicional */}
          <div className="plan-info-grid">
            <div className="info-card">
              <div className="info-icon">
                <FaCalendarAlt />
              </div>
              <div className="info-content">
                <span className="info-label">{t('admin.period')}</span>
                <span className="info-value">{getCurrentMonth()}</span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FaChartLine />
              </div>
              <div className="info-content">
                <span className="info-label">{t('admin.remaining')}</span>
                <span className="info-value">{usage.remaining || 0} {t('admin.queries')}</span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FaUsers />
              </div>
              <div className="info-content">
                <span className="info-label">{t('admin.usersInOrg')}</span>
                <span className="info-value">{usage.organization_users || 1} {t('admin.inOrg')}</span>
              </div>
            </div>
          </div>

          {/* Detalles del plan */}
          <div className="plan-details">
            <h4>{t('admin.planDetails')}</h4>
            <div className="details-list">
              <div className="detail-row">
                <span className="detail-label">{t('admin.usersInOrganization')}</span>
                <span className="detail-value">{usage.organization_users || 1}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('admin.monthlyLimit')}</span>
                <span className="detail-value">{usage.monthly_limit || 1000} {t('admin.queries')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('admin.queriesUsedThisMonth')}</span>
                <span className="detail-value">{usage.monthly_count || 0} {t('admin.queries')}</span>
              </div>
            </div>
          </div>

          {/* Nota de reinicio */}
          <div className="plan-note">
            <p>{t('admin.planNote')}</p>
          </div>
        </>
      ) : (
        <div className="no-data">
          <FaExclamationTriangle className="no-data-icon" />
          <p>{t('admin.noUsageData')}</p>
        </div>
      )}
    </div>
  );
};

export default PlanManager;
